

"use client"

import { useState, useEffect } from "react"
import { AuthLayout } from "@/components/auth/auth-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useRouter } from "@/src/hooks/useRouter"
import { Loader2, AlertCircle } from "lucide-react"
import { getStarterPackage, completeRegistrationWithSession, PackageInfo } from "@/lib/api/registration"
import { getStep1Data, clearRegistrationSession } from "@/lib/utils/registrationSession"
import { useThaiLocationForm } from "@/hooks/useThaiLocationForm"
import { ThaiLocationSelect } from "@/components/forms/ThaiLocationSelect"

export default function RegisterStep3() {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [loadingPackage, setLoadingPackage] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [packageInfo, setPackageInfo] = useState<PackageInfo | null>(null)
  
  const [formData, setFormData] = useState({
    shopName: "",
    email: "",
    address: "",
  })

  // Thai location cascading dropdowns
  const {
    formData: locationData,
    loading: locationLoading,
    provinces,
    amphures,
    districts,
    handleProvinceChange,
    handleAmphureChange,
    handleDistrictChange,
  } = useThaiLocationForm({
    onError: () => setError("ไม่สามารถโหลดข้อมูลที่อยู่ได้ กรุณาลองใหม่อีกครั้ง"),
  })

  // Load session data and package info on mount
  useEffect(() => {
    // Check if session is valid
    const sessionData = getStep1Data()
    if (!sessionData) {
      setError("Session หมดอายุ กรุณาเริ่มต้นการลงทะเบียนใหม่")
      setTimeout(() => {
        router.push("/auth/register/step-2")
      }, 2000)
      return
    }

    // Load package info
    loadPackageInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadPackageInfo = async () => {
    try {
      setLoadingPackage(true)
      const response = await getStarterPackage()
      setPackageInfo(response.data)
    } catch (err: any) {
      console.error("Failed to load package info:", err)
      // Use default values if API fails
      setPackageInfo({
        id: '',
        code: 'starter',
        name: 'แพ็กเกจทดลอง',
        description: 'แพ็กเกจทดลองใช้งานฟรี',
        price: 0,
        includedCredits: 100,
        durationDays: 30,
        expiresAt: '',
        formattedExpiryDate: '-',
        features: []
      })
    } finally {
      setLoadingPackage(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError("") // Clear error when user types
  }

  const handleContinue = async () => {
    setError("")
    setSuccess("")

    // Validation
    if (!formData.shopName || formData.shopName.length < 2) {
      setError("กรุณากรอกชื่อร้านค้า (อย่างน้อย 2 ตัวอักษร)")
      return
    }

    if (formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError("กรุณากรอกอีเมลให้ถูกต้อง")
      return
    }

    // Validate location (optional but must be complete if partially filled)
    const hasPartialLocation =
      locationData.province || locationData.amphure || locationData.tambon
    if (hasPartialLocation) {
      if (!locationData.province) {
        setError("กรุณาเลือกจังหวัด")
        return
      }
      if (!locationData.amphure) {
        setError("กรุณาเลือกเขต / อำเภอ")
        return
      }
      if (!locationData.tambon) {
        setError("กรุณาเลือกแขวง / ตำบล")
        return
      }
    }

    // Get session data
    const sessionData = getStep1Data()
    if (!sessionData) {
      setError("Session หมดอายุ กรุณาเริ่มต้นการลงทะเบียนใหม่")
      setTimeout(() => {
        router.push("/auth/register/step-2")
      }, 2000)
      return
    }

    try {
      setLoading(true)

      // Complete registration with session token
      const response = await completeRegistrationWithSession(
        sessionData.sessionToken,
        {
          shopName: formData.shopName,
          password: sessionData.password,
          confirmPassword: sessionData.password,
          agreeTerms: sessionData.agreeTerms,
          agreePrivacy: sessionData.agreePrivacy,
          email: formData.email || undefined,
          address: formData.address || undefined,
          province: locationData.province || undefined,
          amphure: locationData.amphure || undefined,
          tambon: locationData.tambon || undefined,
          postalCode: locationData.postalCode || undefined,
        }
      )

      // Save tokens and user data
      localStorage.setItem('authToken', response.data.token)
      localStorage.setItem('refreshToken', response.data.refreshToken)
      localStorage.setItem('user', JSON.stringify(response.data.user))

      // Clear registration session
      clearRegistrationSession()

      setSuccess("ลงทะเบียนสำเร็จ! กำลังไปยังหน้ายืนยันตัวตน...")

      // Navigate to KYC verification
      setTimeout(() => {
        router.push("/auth/verify")
      }, 1500)

    } catch (err: any) {
      setError(err.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง")
    } finally {
      setLoading(false)
    }
  }

  const handleViewOtherPackages = () => {
    // Future: implement package selection
    console.log("View other packages clicked")
  }

  // Show loading while checking session
  if (loadingPackage) {
    return (
      <AuthLayout>
        <div className="w-full max-w-md mx-auto flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[var(--brand-primary)]" />
            <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </AuthLayout>
    )
  }

  // Calculate remaining days
  const remainingDays = packageInfo?.durationDays || 30

  return (
    <AuthLayout>
      <div className="w-full max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">THAISLIPCHECK ยืนยันตัวตน</h1>
          <p className="text-[var(--brand-primary)] text-lg font-medium">
            ข้อมูลร้านค้าและแพ็กเกจ
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Shop Icon */}
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 flex items-center justify-center">
              <img src="/icons/shop-welcome-icon.svg" alt="Shop Welcome Icon" className="w-24 h-24" />
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="shopName" className="text-gray-700 font-medium">
              ชื่อร้านค้า <span className="text-red-500">*</span>
            </Label>
            <Input
              id="shopName"
              type="text"
              value={formData.shopName}
              onChange={(e) => handleInputChange("shopName", e.target.value)}
              placeholder="กรอกชื่อร้านค้า"
              className="mt-1 border-[var(--brand-primary)] focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
              disabled={loading}
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-gray-700 font-medium">
              อีเมล <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="example@email.com"
              className="mt-1 border-[var(--brand-primary)] focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
              disabled={loading}
            />
          </div>

          {/* Address (optional) */}
          <div>
            <Label htmlFor="address" className="text-gray-700 font-medium">
              ที่อยู่ <span className="text-gray-400 text-xs font-normal">(ไม่บังคับ)</span>
            </Label>
            <Input
              id="address"
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              placeholder="เลขที่ หมู่ ถนน ซอย (ถ้ามี)"
              className="mt-1 border-[var(--brand-primary)] focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]"
              disabled={loading}
            />
          </div>

          {/* Thai Location - Cascading Dropdowns (optional) */}
          <div className="space-y-3">
            <p className="text-sm text-gray-500">ที่อยู่ <span className="text-gray-400 text-xs">(ไม่บังคับ — ถ้าเลือกต้องกรอกให้ครบ)</span></p>

            <div className="grid grid-cols-2 gap-3">
              <ThaiLocationSelect
                label="จังหวัด"
                value={locationData.province}
                onChange={handleProvinceChange}
                options={provinces}
                placeholder="เลือกจังหวัด"
                loading={locationLoading.provinces}
                disabled={loading || locationLoading.provinces}
              />
              <ThaiLocationSelect
                label="เขต / อำเภอ"
                value={locationData.amphure}
                onChange={handleAmphureChange}
                options={amphures}
                placeholder={locationData.provinceId ? "เลือกเขต / อำเภอ" : "เลือกจังหวัดก่อน"}
                loading={locationLoading.amphures}
                disabled={loading || !locationData.provinceId || locationLoading.amphures}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <ThaiLocationSelect
                label="แขวง / ตำบล"
                value={locationData.tambon}
                onChange={handleDistrictChange}
                options={districts}
                placeholder={locationData.amphureId ? "เลือกแขวง / ตำบล" : "เลือกอำเภอก่อน"}
                loading={locationLoading.districts}
                disabled={loading || !locationData.amphureId || locationLoading.districts}
              />
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">รหัสไปรษณีย์</Label>
                <Input
                  type="text"
                  value={locationData.postalCode}
                  readOnly
                  placeholder="อัตโนมัติเมื่อเลือกตำบล"
                  disabled
                  className="bg-gray-50 border-[var(--brand-primary)]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Package Information */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <p className="text-gray-700 text-sm font-medium">คุณสามารถเพิ่มร้านค้าและบัญชีได้ภายหลัง</p>

          <div className="bg-white rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-center">
              <div className="font-bold">
                <span className="me-2">แพ็กเกจ</span>
                <span className="text-[var(--brand-primary)]">
                  {packageInfo?.name || 'ทดลอง'}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-gray-600">คงเหลือ</span>
                <span className="text-[var(--brand-primary)] font-semibold">
                  {remainingDays} วัน
                </span>
              </div>
            </div>

            <hr className="my-3 border-gray-200" />

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span>ราคา</span>
                <span className="font-medium">{packageInfo?.price || 0} บาท</span>
              </div>
              <div className="flex justify-between">
                <span>จำนวนสลิปครั้ง</span>
                <span className="text-[var(--brand-primary)] font-medium">
                  {packageInfo?.includedCredits || 100}/{packageInfo?.includedCredits || 100} สลิป
                </span>
              </div>
              {packageInfo?.qrCodeQuota && (
                <div className="flex justify-between">
                  <span>สร้าง QR Code</span>
                  <span className="text-[var(--brand-primary)] font-medium">
                    {packageInfo?.qrCodeQuota || 10}/{packageInfo?.qrCodeQuota || 10} ครั้ง
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span>วันที่หมดอายุ</span>
                <span className="font-medium">
                  {packageInfo?.formattedExpiryDate || '-'}
                </span>
              </div>
            </div>

            <div className="pt-2">
              <div className="flex justify-between items-center">
                <span className="font-medium">สถานะ:</span>
                <span className="text-[var(--brand-primary)] font-medium">พร้อมใช้งาน</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          {/* Hide "ดูแพ็กเกจอื่น" button as requested */}
          <Button
            variant="outline"
            onClick={handleViewOtherPackages}
            className="hidden flex-1 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white bg-transparent"
            disabled
          >
            ดูแพ็กเกจอื่น
          </Button>
          
          <Button
            onClick={handleContinue}
            disabled={loading}
            className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/90 text-white disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                กำลังดำเนินการ...
              </>
            ) : (
              "ยืนยัน"
            )}
          </Button>
        </div>

        {/* Help Text */}
        <p className="text-center text-sm text-gray-500">
          หลังจากยืนยันแล้ว คุณจะต้องทำการยืนยันตัวตนเพื่อใช้งานระบบ
        </p>
      </div>
    </AuthLayout>
  )
}
